import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  AlertTriangle,
  Loader2,
  MoreVertical,
  OctagonX,
} from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";
import { IngestRunActivityCell } from "@/components/IngestRunActivityCell";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintWithGlyph, HoverHintContent, HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { formatCronForDisplay } from "@/lib/cron-hint";
import {
  INGEST_CONTROL_QUERY_LIVE,
  freshsalesPushWebhookApplies,
  ingestSourceFamily,
  type IngestControlEntity,
  type IngestControlListResponse,
  type IngestSourceFamily,
  type MegaventoryWebhookReceiverSlug,
} from "@/lib/ingest-control-api";
import { useNowTick } from "@/lib/useNowTick";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { useErpIngestLiveQueries } from "@/lib/useErpIngestLiveQueries";
import { formatTimestamp, ingestRunDurationLabel, relativeTime, cn } from "@/lib/utils";

const MV_INGEST_PAGE_HELP =
  "One row per ingest entity, including Megaventory, Freshsales, and other sources on the same scheduler. Click into an entity to drill into its runs, events, and findings. Background jobs that are not ingest (invoice matching, labor journals) stay off this page.";

const DATA_CONNECTORS_READ_ONLY = "Your role has read-only access to the Data connectors hub module.";

const CLEAR_STUCK_RUN_HELP =
  "Marks this run as failed if it is still running in the database. Use when a process exited without finishing. This does not stop a worker process; if a job is still active, it may still write a final status when it completes.";

const N8N_ONLY_ENTITY_HELP =
  "No TypeScript ingest module for this entity yet. It remains n8n-only in code until ported.";

const SWEEP_NOT_APPLICABLE_HELP =
  "Sweep for differences does not apply: each live ingest already replaces the full snapshot in the database, so row-by-row drift classification would add no value.";

/**
 * Dashboard "Schedule" column: prefer live ingest cron when set; otherwise show sweep cron (webhook-first entities).
 */
function megaventoryReceiverDescription(receiver: MegaventoryWebhookReceiverSlug | null | undefined): string {
  if (receiver == null) {
    return "";
  }
  switch (receiver) {
    case "document":
      return "Megaventory document listener at /api/webhooks/megaventory/document (shared URL; each ingest row is selected by DocumentTypeId on the callback JSON).";
    case "product":
      return "Megaventory product listener (ProductId replay).";
    case "sales_order":
      return "Megaventory sales order listener (SalesOrderId replay).";
    case "purchase_order":
      return "Megaventory purchase order listener (PurchaseOrderId replay).";
    case "work_order":
      return "Megaventory work order listener (WorkOrderId replay).";
    case "supplier":
      return "Megaventory Supplier* events listener (SupplierClientId replay for supplier ingest).";
    case "client":
      return "Megaventory Client* events listener (SupplierClientId replay for customer ingest).";
    default:
      return "";
  }
}

function formatIngestScheduleForDashboard(entity: Pick<IngestControlEntity, "schedule_cron" | "sweep_schedule_cron">): {
  label: string;
  title: string;
} {
  const live = entity.schedule_cron?.trim() ?? "";
  if (live !== "") {
    return formatCronForDisplay(entity.schedule_cron);
  }
  const sweep = entity.sweep_schedule_cron?.trim() ?? "";
  if (sweep !== "") {
    const sweepDisplay = formatCronForDisplay(entity.sweep_schedule_cron);
    return {
      label: `Sweep: ${sweepDisplay.label}`,
      title: `No scheduled live ingest. ${sweepDisplay.title}`,
    };
  }
  return formatCronForDisplay(null);
}

interface IngestRun {
  id: number;
  entity: string;
  mode: string;
  started_at: string;
  ended_at: string | null;
  status: "running" | "success" | "partial" | "failed";
  records_seen: number;
  records_failed: number;
  records_upserted: number;
  progress_phase?: string | null;
  progress_label?: string | null;
  progress_current?: number | null;
  progress_total?: number | null;
}

function statusTone(status: IngestRun["status"]): BadgeTone {
  switch (status) {
    case "success":
      return "success";
    case "partial":
      return "warning";
    case "failed":
      return "danger";
    case "running":
      return "info";
  }
}

/** For each entity, keep the run with the latest started_at (within the given list). */
function computeLatestRunByEntity(runs: IngestRun[]): Map<string, IngestRun> {
  const map = new Map<string, IngestRun>();
  for (const run of runs) {
    const prev = map.get(run.entity);
    if (!prev) {
      map.set(run.entity, run);
      continue;
    }
    if (new Date(run.started_at).getTime() > new Date(prev.started_at).getTime()) {
      map.set(run.entity, run);
    }
  }
  return map;
}

function isSweepMode(mode: string | undefined | null): boolean {
  return mode === "sweep_report" || mode === "sweep_apply";
}

const RECENT_RUNS_PAGE_SIZE = 10;

function isDryRunMode(mode: string | undefined | null): boolean {
  return mode === "dry_run";
}

function queryErrorText(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

function escapeForCssSelectorAttribute(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value;
}

export function Dashboard() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("erp");
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<AnchoredMenuPanelBox | null>(null);
  const entityTableScrollRef = useRef<HTMLDivElement | null>(null);
  /** Tracks which run ids were `running` on the last summary sample; used to refresh ingest-control only when a run leaves that state. */
  const summaryRunningRunIdsRef = useRef<Set<number>>(new Set());
  /** Drives the spinner: which /api/run-now mode is in flight (sweep is distinct from a normal "run" in the last run column). */
  const [uiRunInFlight, setUiRunInFlight] = useState<Record<string, "live" | "dry_run" | "sweep_report">>({});
  const uiRunInFlightRef = useRef<Set<string>>(new Set());
  const [runError, setRunError] = useState<string | null>(null);
  const [recentRunsPage, setRecentRunsPage] = useState(0);
  const [sourceFamily, setSourceFamily] = useState<IngestSourceFamily | "all">("all");

  const closeActionMenu = useCallback(() => {
    setOpenActionMenu(null);
    setMenuPos(null);
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (openActionMenu == null) return;
    const sel = `[data-entity-menu-anchor="${escapeForCssSelectorAttribute(openActionMenu)}"]`;
    const trigger = document.querySelector(sel) as HTMLButtonElement | null;
    if (!trigger) return;
    setMenuPos(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: 14 * 16,
        horizontalAlign: "end",
        gap: 4,
        maxHeightCap: Math.min(520, Math.floor(window.innerHeight * 0.88)),
      }),
    );
  }, [openActionMenu]);

  const controlQuery = useQuery({
    queryKey: ["ingest-control"],
    queryFn: () => apiFetch<IngestControlListResponse>("/api/ingest-control"),
    ...INGEST_CONTROL_QUERY_LIVE,
  });

  useErpIngestLiveQueries();

  const hasUiRunInFlight = Object.keys(uiRunInFlight).length > 0;

  const runsSummaryQuery = useQuery({
    queryKey: ["ingest-runs", "recent-summary"],
    queryFn: () =>
      apiFetch<{ runs: IngestRun[]; total?: number }>("/api/ingest-runs", {
        query: { limit: 100, skip_total: true },
      }),
  });

  const runsPageQuery = useQuery({
    queryKey: ["ingest-runs", "recent", recentRunsPage],
    queryFn: () =>
      apiFetch<{ runs: IngestRun[]; total: number }>("/api/ingest-runs", {
        query: { limit: RECENT_RUNS_PAGE_SIZE, offset: recentRunsPage * RECENT_RUNS_PAGE_SIZE },
      }),
  });

  const recentTotal = runsPageQuery.data?.total ?? 0;
  const recentPageCount = recentTotal === 0 ? 0 : Math.ceil(recentTotal / RECENT_RUNS_PAGE_SIZE);

  useEffect(() => {
    if (recentPageCount === 0) return;
    setRecentRunsPage((pageIndex) => (pageIndex >= recentPageCount ? recentPageCount - 1 : pageIndex));
  }, [recentPageCount]);

  const summaryRuns = runsSummaryQuery.data?.runs ?? [];
  const pageRuns = runsPageQuery.data?.runs ?? [];
  const hasRunningRunRow = useMemo(
    () =>
      summaryRuns.some((run) => run.status === "running") ||
      pageRuns.some((run) => run.status === "running"),
    [summaryRuns, pageRuns],
  );
  const durationClockMs = useNowTick(hasRunningRunRow || hasUiRunInFlight);

  const abortRunMutation = useMutation({
    mutationFn: (runId: number) =>
      apiFetch<{ run: { id: number } }>(`/api/ingest-runs/${runId}/abort`, { method: "POST" }),
    onMutate: () => {
      setRunError(null);
    },
    onError: (err) => {
      setRunError(err instanceof Error ? err.message : String(err));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["ingest-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["ingest-control"] });
    },
  });

  const latestRunByEntity = useMemo(
    () => computeLatestRunByEntity(runsSummaryQuery.data?.runs ?? []),
    [runsSummaryQuery.data?.runs],
  );

  const runRowFromMenu = useCallback(
    (entity: string, mode: "live" | "dry_run" | "sweep_report") => {
      if (uiRunInFlightRef.current.has(entity)) return;
      uiRunInFlightRef.current.add(entity);
      closeActionMenu();
      setRunError(null);
      setUiRunInFlight((m) => ({ ...m, [entity]: mode }));
      void queryClient.refetchQueries({ queryKey: ["ingest-runs", "recent-summary"] });
      void queryClient.refetchQueries({ queryKey: ["ingest-runs", "recent"] });
      void (async () => {
        try {
          await apiFetch("/api/run-now", { method: "POST", body: { entity, mode } });
        } catch (err) {
          setRunError(err instanceof Error ? err.message : String(err));
        } finally {
          uiRunInFlightRef.current.delete(entity);
          setUiRunInFlight((m) => {
            const next = { ...m };
            delete next[entity];
            return next;
          });
          void queryClient.invalidateQueries({ queryKey: ["ingest-control"] });
          void queryClient.invalidateQueries({ queryKey: ["ingest-runs"] });
        }
      })();
    },
    [queryClient, closeActionMenu],
  );

  /** When null, the API omitted `registered_entities` (older build) — do not gate the menu. */
  const registeredEntitySet = useMemo(() => {
    const list = controlQuery.data?.registered_entities;
    if (list == null) return null;
    return new Set(list);
  }, [controlQuery.data?.registered_entities]);

  const actionMenuState = useMemo(() => {
    if (openActionMenu == null) return null;
    const row = controlQuery.data?.entities.find((e) => e.entity === openActionMenu);
    if (!row) return null;
    const latest = latestRunByEntity.get(openActionMenu);
    const anyRunInProgress =
      latest?.status === "running" || Boolean(uiRunInFlight[openActionMenu]);
    const appImplementsEntity =
      registeredEntitySet == null || registeredEntitySet.has(openActionMenu);
    const sweepReportSupported = row.sweep_report_supported !== false;
    return { row, runActionsDisabled: anyRunInProgress, appImplementsEntity, sweepReportSupported };
  }, [openActionMenu, controlQuery.data?.entities, latestRunByEntity, uiRunInFlight, registeredEntitySet]);

  useLayoutEffect(() => {
    if (openActionMenu == null) {
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = () => {
      updateMenuPosition();
    };
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.visualViewport?.addEventListener("resize", onScrollOrResize);
    window.visualViewport?.addEventListener("scroll", onScrollOrResize);
    const scrollEl = entityTableScrollRef.current;
    scrollEl?.addEventListener("scroll", onScrollOrResize);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.visualViewport?.removeEventListener("resize", onScrollOrResize);
      window.visualViewport?.removeEventListener("scroll", onScrollOrResize);
      scrollEl?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [openActionMenu, updateMenuPosition]);

  useEffect(() => {
    if (openActionMenu === null) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.("[data-entity-menu-panel], [data-entity-menu-anchor]")) {
        return;
      }
      closeActionMenu();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeActionMenu();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openActionMenu, closeActionMenu]);

  useEffect(() => {
    if (openActionMenu == null) return;
    if (actionMenuState != null) return;
    if (!controlQuery.isFetched) return;
    closeActionMenu();
  }, [openActionMenu, actionMenuState, controlQuery.isFetched, closeActionMenu]);

  useEffect(() => {
    if (!runsSummaryQuery.isSuccess || !runsSummaryQuery.data?.runs) {
      return;
    }
    const runs = runsSummaryQuery.data.runs;
    const runningNow = new Set(
      runs.filter((run) => run.status === "running").map((run) => run.id),
    );
    const prev = summaryRunningRunIdsRef.current;
    let aRunLeftRunningState = false;
    if (prev.size > 0) {
      for (const id of prev) {
        if (runningNow.has(id)) continue;
        const row = runs.find((run) => run.id === id);
        if (row == null || row.status !== "running") {
          aRunLeftRunningState = true;
          break;
        }
      }
    }
    summaryRunningRunIdsRef.current = runningNow;
    if (aRunLeftRunningState) {
      void queryClient.invalidateQueries({ queryKey: ["ingest-control"] });
    }
  }, [queryClient, runsSummaryQuery.data, runsSummaryQuery.isSuccess]);

  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-slate-900">Ingest</h1>
          <HoverHintWithGlyph hoverHint={MV_INGEST_PAGE_HELP} size="md" />
        </div>
        {runError ? <p className="mt-2 text-sm text-red-700">{runError}</p> : null}
        {controlQuery.isError || runsPageQuery.isError || runsSummaryQuery.isError ? (
          <div
            className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
            role="alert"
          >
            {controlQuery.isError ? (
              <p>
                <span className="font-medium">Could not load entities</span> ({queryErrorText(controlQuery.error)}).
              </p>
            ) : null}
            {runsPageQuery.isError || runsSummaryQuery.isError ? (
              <p className={controlQuery.isError ? "mt-2" : ""}>
                <span className="font-medium">Could not load recent runs</span> (
                {queryErrorText(runsPageQuery.error ?? runsSummaryQuery.error)}).
              </p>
            ) : null}
          </div>
        ) : null}
      </header>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Entities</CardTitle>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by source">
            {(
              [
                ["all", "All"],
                ["megaventory", "Megaventory"],
                ["freshsales", "Freshsales"],
                ["other", "Other"],
              ] as const
            ).map(([id, label]) => (
              <Button
                key={id}
                type="button"
                variant={sourceFamily === id ? "primary" : "secondary"}
                className="h-8 px-2 text-xs"
                onClick={() => setSourceFamily(id)}
              >
                {label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div ref={entityTableScrollRef} className="w-full">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Entity</th>
                  <th className="px-4 py-2 font-medium">
                    <HoverHintContent hoverHint="Inbound HTTP callbacks that start a run for this entity. Megaventory rows show EventSubscription status. Freshsales company and contact webhooks replay customer, freshsales_contact, and rep_staff.">
                      <span>Webhook</span>
                    </HoverHintContent>
                  </th>
                  <th className="px-4 py-2 font-medium">
                    <HoverHintContent hoverHint="Shows live ingest cron when the scheduler runs incremental pulls; otherwise shows the sweep cron (typical for webhook-driven entities with no live schedule).">
                      <span>Schedule</span>
                    </HoverHintContent>
                  </th>
                  <th className="px-4 py-2 font-medium">
                    <HoverHintContent hoverHint="When the last live, replay, or staging run finished. Dry run, diff, and sweep do not change this column; see Recent runs or Last sweep / diff fields.">
                      <span>Last run</span>
                    </HoverHintContent>
                  </th>
                  <th className="px-4 py-2 font-medium">Last success</th>
                  <th className="px-4 py-2 font-medium">Last sweep</th>
                  <th className="px-4 py-2 font-medium">Health</th>
                  <th className="w-14 px-2 py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {controlQuery.data?.entities
                  .filter((entity) => sourceFamily === "all" || ingestSourceFamily(entity.entity) === sourceFamily)
                  .map((entity) => {
                  const scheduleDisplay = formatIngestScheduleForDashboard(entity);
                  const sweep = entity.last_sweep_summary as
                    | { drift?: number; missing?: number; orphan?: number }
                    | null
                    | undefined;
                  const drift = sweep?.drift ?? 0;
                  const missing = sweep?.missing ?? 0;
                  const orphan = sweep?.orphan ?? 0;
                  const sweepFindings = drift + missing + orphan;
                  const sweepReportSupported = entity.sweep_report_supported !== false;
                  const webhookSecretConfigured = controlQuery.data?.megaventory_webhook_secret_configured ?? false;
                  const freshsalesSecretConfigured =
                    controlQuery.data?.freshsales_webhook_secret_configured ?? false;
                  const freshsalesPush = entity.freshsales_push_webhook === true || freshsalesPushWebhookApplies(entity.entity);
                  const wh = entity.megaventory_webhook;
                  const latest = latestRunByEntity.get(entity.entity);
                  const clientMode = uiRunInFlight[entity.entity];
                  const anyServerRunning = latest?.status === "running";
                  const isDryRunInProgress =
                    (anyServerRunning && isDryRunMode(latest?.mode)) || clientMode === "dry_run";
                  const isLastRunInProgress =
                    (anyServerRunning && !isSweepMode(latest?.mode) && !isDryRunMode(latest?.mode)) ||
                    clientMode === "live";
                  const isSweepInProgress =
                    sweepReportSupported &&
                    ((anyServerRunning && isSweepMode(latest?.mode)) || clientMode === "sweep_report");
                  const isRowWorkInProgress =
                    isLastRunInProgress || isSweepInProgress || isDryRunInProgress;
                  const webhookHelpText =
                    wh?.applicable === true
                      ? [
                          megaventoryReceiverDescription(wh.receiver),
                          wh.subscriptions_registered
                            ? wh.receiver_subscription_count != null &&
                              wh.receiver_subscription_count !== wh.subscription_count
                              ? `${wh.subscription_count} of ${wh.receiver_subscription_count} Megaventory EventSubscription rows on the hub apply to this entity (shared ${wh.receiver} listener).`
                              : `${wh.subscription_count} Megaventory EventSubscription row${
                                  wh.subscription_count === 1 ? "" : "s"
                                } stored on the hub for this entity.`
                            : wh.receiver_subscription_count != null && wh.receiver_subscription_count > 0
                              ? `No EventSubscription rows apply to this entity; ${wh.receiver_subscription_count} row${
                                  wh.receiver_subscription_count === 1 ? "" : "s"
                                } exist for other event types on the shared ${wh.receiver} listener.`
                              : "No Megaventory EventSubscription ids stored yet for this entity (run API startup ensure or POST the subscription route).",
                          wh.subscriptions_updated_at
                            ? `Subscriptions last renewed: ${formatTimestamp(wh.subscriptions_updated_at)}.`
                            : "",
                          webhookSecretConfigured
                            ? ""
                            : "This API host has no INTEGRATOR_MEGAVENTORY_WEBHOOK_SECRET; webhook routes are not registered.",
                          "Latest ingest run whose triggered_by came from a Megaventory HTTP callback for this entity.",
                        ]
                          .filter((line) => line !== "")
                          .join(" ")
                      : "";
                  return (
                    <tr
                      key={entity.entity}
                      className={cn("hover:bg-slate-50", isRowWorkInProgress && "bg-slate-50/80")}
                      aria-busy={isRowWorkInProgress}
                    >
                      <td className="px-4 py-3">
                        <Link to={`/erp/entity/${entity.entity}`} className="font-medium text-brand-700 hover:underline">
                          {entity.entity}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {wh?.applicable ? (
                          <HoverHintContent hoverHint={webhookHelpText} block className="max-w-[20rem]">
                            <div className="flex max-w-[20rem] flex-col gap-0.5 text-xs text-slate-500">
                              <div className="inline-flex flex-wrap items-center gap-1.5">
                                {wh.subscriptions_registered ? (
                                  <>
                                    <CheckCircle2
                                      className="h-4 w-4 shrink-0 text-emerald-600"
                                      aria-hidden
                                    />
                                    <span className="sr-only">Megaventory subscription registered.</span>
                                  </>
                                ) : webhookSecretConfigured ? (
                                  <Badge tone="warning">Not registered</Badge>
                                ) : (
                                  <Badge tone="muted">Receivers off</Badge>
                                )}
                                <span>
                                  Last callback:{" "}
                                  {wh.last_webhook_run_at ? relativeTime(wh.last_webhook_run_at) : "never"}
                                </span>
                              </div>
                              {freshsalesPush ? (
                                <span className="text-xs text-slate-600">
                                  {freshsalesSecretConfigured
                                    ? "Also Freshsales company webhook"
                                    : "Freshsales company webhook off"}
                                </span>
                              ) : null}
                            </div>
                          </HoverHintContent>
                        ) : freshsalesPush ? (
                          <HoverHintContent
                            hoverHint={
                              freshsalesSecretConfigured
                                ? "Freshsales company or contact workflows POST /api/webhooks/freshsales/entity, and the hub replays this entity for that id."
                                : "This entity is replayed from the Freshsales entity webhook, but INTEGRATOR_FRESHSALES_WEBHOOK_SECRET is not set on this API host, so the route is not registered."
                            }
                          >
                            <span className="text-xs text-slate-600">
                              {freshsalesSecretConfigured ? "Freshsales push" : "Freshsales receivers off"}
                            </span>
                          </HoverHintContent>
                        ) : (
                          <HoverHintContent hoverHint="This entity is pulled on a schedule. It does not have an inbound Megaventory or Freshsales webhook.">
                            <span className="text-slate-400">Scheduled only</span>
                          </HoverHintContent>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <HoverHintContent hoverHint={scheduleDisplay.title} block>
                          <span className="block min-w-0">{scheduleDisplay.label}</span>
                        </HoverHintContent>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="flex flex-col gap-0.5">
                          <HoverHintContent hoverHint="Time the last live, replay, or staging run finished. Dry run, diff, sweep: see Recent runs or entity detail; they do not move this column.">
                            <span className="inline-flex items-center gap-1.5">
                              <span>{relativeTime(entity.last_run_at)}</span>
                              {isLastRunInProgress ? (
                                <HoverHintTrigger
                                  align="start"
                                  trigger={
                                    <span className="inline-flex shrink-0" aria-label="In progress">
                                      <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-600" aria-hidden />
                                    </span>
                                  }
                                >
                                  Live or other non-dry run in progress
                                </HoverHintTrigger>
                              ) : null}
                            </span>
                          </HoverHintContent>
                          {isDryRunInProgress ? (
                            <HoverHintContent hoverHint="Does not change Last run. Outcome is in Recent runs when finished.">
                              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                                <Loader2
                                  className="h-3 w-3 shrink-0 animate-spin text-brand-600"
                                  aria-hidden
                                />
                                Dry run in progress
                              </span>
                            </HoverHintContent>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{relativeTime(entity.last_success_at)}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          {!sweepReportSupported ? (
                            <HoverHintContent
                              hoverHint="Sweep for differences does not apply: each live ingest already replaces the full snapshot, so last sweep time is not shown."
                              block
                            >
                              <span className="text-slate-400">Disabled</span>
                            </HoverHintContent>
                          ) : entity.last_sweep_at ? (
                            <>
                              <span>{relativeTime(entity.last_sweep_at)}</span>
                              {sweepFindings > 0 ? (
                                <Badge tone="warning" className="shrink-0">
                                  {sweepFindings} findings
                                </Badge>
                              ) : null}
                            </>
                          ) : (
                            <span className="text-slate-400">never</span>
                          )}
                          {isSweepInProgress ? (
                            <HoverHintTrigger
                              align="start"
                              trigger={
                                <span className="inline-flex shrink-0" aria-label="Sweep in progress">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-600" aria-hidden />
                                </span>
                              }
                            >
                              Sweep in progress
                            </HoverHintTrigger>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {(entity.open_problems_count ?? 0) > 0 ? (
                          <HoverHintContent hoverHint="Unresolved ingest events — open Findings to review and resolve">
                            <Link
                              to={`/erp/findings?entity=${encodeURIComponent(entity.entity)}&outcome=problems`}
                              className="inline-flex"
                            >
                              <Badge tone="warning">
                                <AlertCircle className="mr-1 h-3 w-3" />
                                {entity.open_problems_count} in Findings
                              </Badge>
                            </Link>
                          </HoverHintContent>
                        ) : entity.source === "app_only" && entity.last_success_at ? (
                          <Badge tone="success">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            healthy
                          </Badge>
                        ) : (
                          <Badge tone="muted">—</Badge>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex justify-end">
                          <ReadOnlyHoverLayer
                            active={!hubWritesAllowed}
                            hoverHint={DATA_CONNECTORS_READ_ONLY}
                            layout="inline"
                          >
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 shrink-0 p-0 text-slate-500 hover:text-slate-900"
                              data-entity-menu-anchor={entity.entity}
                              aria-label={`Actions for ${entity.entity}`}
                              aria-haspopup="menu"
                              aria-expanded={openActionMenu === entity.entity}
                              disabled={!hubWritesAllowed}
                              onClick={() => {
                                if (openActionMenu === entity.entity) {
                                  closeActionMenu();
                                  return;
                                }
                                setOpenActionMenu(entity.entity);
                              }}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </ReadOnlyHoverLayer>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">Duration</th>
                <th className="px-4 py-2 font-medium">Entity</th>
                <th className="px-4 py-2 font-medium">Mode</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Activity</th>
                <th className="px-4 py-2 font-medium">Seen / Upserted / Failed</th>
                <th className="px-4 py-2 font-medium">
                  <span className="sr-only">Clear stuck run</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {runsPageQuery.data?.runs.map((run) => (
                <tr key={run.id}>
                  <td className="px-4 py-2 text-slate-600">
                    <Clock className="mr-1 inline h-3 w-3 text-slate-400" />
                    {formatTimestamp(run.started_at)}
                  </td>
                  <td className="px-4 py-2">
                    {(() => {
                      const { text, isLive } = ingestRunDurationLabel(run, durationClockMs);
                      return (
                        <HoverHintContent
                          hoverHint={
                            isLive
                              ? "Elapsed time since this run started (updates every second)."
                              : "Time from start to end."
                          }
                        >
                          <span
                            className={cn(
                              "inline-block font-mono text-xs tabular-nums text-slate-700",
                              isLive && "font-medium text-brand-700",
                            )}
                          >
                            {text}
                            {isLive ? (
                              <Loader2 className="ml-1 inline h-3 w-3 animate-spin text-brand-600" aria-hidden />
                            ) : null}
                          </span>
                        </HoverHintContent>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2">
                    <Link to={`/erp/entity/${run.entity}`} className="text-brand-700 hover:underline">{run.entity}</Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{run.mode}</td>
                  <td className="px-4 py-2">
                    <Badge tone={statusTone(run.status)}>{run.status}</Badge>
                  </td>
                  <td className="px-4 py-2 align-top">
                    <IngestRunActivityCell run={run} />
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {run.records_seen}
                    {" / "}
                    <span className="text-slate-900">{run.records_upserted}</span>
                    {" / "}
                    {run.records_failed > 0 ? (
                      <span className="font-medium text-red-700">
                        <AlertTriangle className="mr-1 inline h-3 w-3" />
                        {run.records_failed}
                      </span>
                    ) : (
                      <span>{run.records_failed}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {run.status === "running" ? (
                      <ReadOnlyHoverLayer
                        active={!hubWritesAllowed}
                        hoverHint={DATA_CONNECTORS_READ_ONLY}
                        layout="inline"
                      >
                        {hubWritesAllowed ? (
                          <HoverHintTrigger
                            trigger={
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="whitespace-nowrap border-red-200 text-red-800 hover:bg-red-50"
                                isLoading={abortRunMutation.isPending && abortRunMutation.variables === run.id}
                                onClick={() => {
                                  abortRunMutation.mutate(run.id);
                                }}
                              >
                                <OctagonX className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                                Clear stuck status
                              </Button>
                            }
                          >
                            {CLEAR_STUCK_RUN_HELP}
                          </HoverHintTrigger>
                        ) : (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="whitespace-nowrap border-red-200 text-red-800 hover:bg-red-50"
                            disabled
                            isLoading={abortRunMutation.isPending && abortRunMutation.variables === run.id}
                          >
                            <OctagonX className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                            Clear stuck status
                          </Button>
                        )}
                      </ReadOnlyHoverLayer>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
            <span>
              {recentTotal === 0 ? (
                "No runs yet."
              ) : (
                <>
                  Page {recentRunsPage + 1} of {recentPageCount} ({recentTotal} total)
                </>
              )}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={recentRunsPage <= 0 || runsPageQuery.isFetching || recentTotal === 0}
                onClick={() => setRecentRunsPage((pageIndex) => Math.max(0, pageIndex - 1))}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={
                  recentPageCount === 0 ||
                  recentRunsPage >= recentPageCount - 1 ||
                  runsPageQuery.isFetching
                }
                onClick={() => setRecentRunsPage((pageIndex) => pageIndex + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
      {actionMenuState && menuPos
        ? createPortal(
            <ul
              data-entity-menu-panel
              role="menu"
              className="fixed overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm text-slate-800 shadow-lg"
              style={{
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                maxHeight: menuPos.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
              {actionMenuState.row.source === "app_only" ? (
                <li>
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY} layout="block">
                    {hubWritesAllowed && !actionMenuState.appImplementsEntity ? (
                      <HoverHintContent hoverHint={N8N_ONLY_ENTITY_HELP} block>
                        <button
                          type="button"
                          role="menuitem"
                          className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                          disabled={
                            !hubWritesAllowed ||
                            actionMenuState.runActionsDisabled ||
                            !actionMenuState.appImplementsEntity
                          }
                          onClick={() => {
                            runRowFromMenu(actionMenuState.row.entity, "live");
                          }}
                        >
                          Run now
                        </button>
                      </HoverHintContent>
                    ) : (
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                        disabled={
                          !hubWritesAllowed ||
                          actionMenuState.runActionsDisabled ||
                          !actionMenuState.appImplementsEntity
                        }
                        onClick={() => {
                          runRowFromMenu(actionMenuState.row.entity, "live");
                        }}
                      >
                        Run now
                      </button>
                    )}
                  </ReadOnlyHoverLayer>
                </li>
              ) : null}
              <li>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY} layout="block">
                  {hubWritesAllowed && !actionMenuState.appImplementsEntity ? (
                    <HoverHintContent hoverHint={N8N_ONLY_ENTITY_HELP} block>
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                        disabled={
                          !hubWritesAllowed ||
                          actionMenuState.runActionsDisabled ||
                          !actionMenuState.appImplementsEntity
                        }
                        onClick={() => {
                          runRowFromMenu(actionMenuState.row.entity, "dry_run");
                        }}
                      >
                        Dry run now
                      </button>
                    </HoverHintContent>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                      disabled={
                        !hubWritesAllowed ||
                        actionMenuState.runActionsDisabled ||
                        !actionMenuState.appImplementsEntity
                      }
                      onClick={() => {
                        runRowFromMenu(actionMenuState.row.entity, "dry_run");
                      }}
                    >
                      Dry run now
                    </button>
                  )}
                </ReadOnlyHoverLayer>
              </li>
              <li>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY} layout="block">
                  {hubWritesAllowed && !actionMenuState.appImplementsEntity ? (
                    <HoverHintContent hoverHint={N8N_ONLY_ENTITY_HELP} block>
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                        disabled={
                          !hubWritesAllowed ||
                          actionMenuState.runActionsDisabled ||
                          !actionMenuState.appImplementsEntity
                        }
                        onClick={() => {
                          runRowFromMenu(actionMenuState.row.entity, "sweep_report");
                        }}
                      >
                        Sweep for differences
                      </button>
                    </HoverHintContent>
                  ) : hubWritesAllowed &&
                    actionMenuState.appImplementsEntity &&
                    !actionMenuState.sweepReportSupported ? (
                    <HoverHintContent hoverHint={SWEEP_NOT_APPLICABLE_HELP} block>
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full px-3 py-2 text-left text-slate-400 hover:bg-slate-50 disabled:opacity-100"
                        disabled
                      >
                        Sweep for differences (not applicable)
                      </button>
                    </HoverHintContent>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full px-3 py-2 text-left hover:bg-slate-100 disabled:opacity-50"
                      disabled={
                        !hubWritesAllowed ||
                        actionMenuState.runActionsDisabled ||
                        !actionMenuState.appImplementsEntity
                      }
                      onClick={() => {
                        runRowFromMenu(actionMenuState.row.entity, "sweep_report");
                      }}
                    >
                      Sweep for differences
                    </button>
                  )}
                </ReadOnlyHoverLayer>
              </li>
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
